import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const UPPERCASE_REGEXP = /[A-Z]/g;
const DIGIT_REGEXP = /\d/g;
const SPECIAL_CHARACTER_REGEXP = /[!@#$%^&*()_\-+=\[\]{};':"\\|,.<>/?`~]/g;

export interface IAgentPasswordSettingsData {
  active: boolean;
  min_length: number;
  digits: { amount: number };
  letters: { amount: number; uppercase: number; lowercase: number };
  special_characters: { amount: number };
}

export interface IAgentPasswordRequirementData {
  id: 'minLength' | 'uppercase' | 'specialCharacter' | 'number';
  label: string;
  isValid: boolean;
}

export interface IAgentPasswordComplexityLocaleData {
  passwordRequirementMinLength: string;
  passwordRequirementUppercase: string;
  passwordRequirementSpecial: string;
  passwordRequirementNumber: string;
  passwordRequirementsText: string;
  passwordRequirementsTooltip: string;
}

export interface IUseAgentPasswordComplexityPayload {
  password: string;
  locale: IAgentPasswordComplexityLocaleData;
}

export interface IValidateAgentPasswordComplexityPayload extends IUseAgentPasswordComplexityPayload {
  settings: IAgentPasswordSettingsData | null | undefined;
}

export interface IValidateAgentPasswordComplexityData {
  requirements: IAgentPasswordRequirementData[];
  validRequirementsCount: number;
  isValid: boolean;
  isStarted: boolean;
  activeBarsCount: number;
  activeColor: string;
  requirementText: string;
  requirementsTooltip: string;
}

export type IUseAgentPasswordComplexityData = IValidateAgentPasswordComplexityData;

export const validateAgentPasswordComplexity = ({
  password,
  locale,
  settings,
}: IValidateAgentPasswordComplexityPayload): IValidateAgentPasswordComplexityData => {
  const rules: {
    id: IAgentPasswordRequirementData['id'];
    label: string;
    minimum: number;
    actual: number;
  }[] = [
    {
      id: 'minLength',
      label: locale.passwordRequirementMinLength,
      minimum: settings?.min_length ?? 0,
      actual: password.length,
    },
    {
      id: 'uppercase',
      label: locale.passwordRequirementUppercase,
      minimum: settings?.letters?.uppercase ?? 0,
      actual: password.match(UPPERCASE_REGEXP)?.length ?? 0,
    },
    {
      id: 'specialCharacter',
      label: locale.passwordRequirementSpecial,
      minimum: settings?.special_characters?.amount ?? 0,
      actual: password.match(SPECIAL_CHARACTER_REGEXP)?.length ?? 0,
    },
    {
      id: 'number',
      label: locale.passwordRequirementNumber,
      minimum: settings?.digits?.amount ?? 0,
      actual: password.match(DIGIT_REGEXP)?.length ?? 0,
    },
  ];

  const requirements: IAgentPasswordRequirementData[] = settings?.active
    ? rules
        .filter(rule => rule.minimum > 0)
        .map(rule => ({
          id: rule.id,
          label: rule.label.replace('<count>', String(rule.minimum)),
          isValid: rule.actual >= rule.minimum,
        }))
    : [];

  const validRequirementsCount = requirements.filter(requirement => requirement.isValid).length;
  const isStarted = password.length > 0;
  const isValid = !!settings && isStarted && validRequirementsCount === requirements.length;
  const completedShare = requirements.length > 0 ? validRequirementsCount / requirements.length : 0;
  const activeColor = isValid ? '#1CCC77' : completedShare >= 0.5 ? '#FFBC0F' : '#D31C32';
  const activeBarsCount = isStarted && requirements.length > 0 ? (isValid ? 3 : completedShare > 0.5 ? 2 : 1) : 0;

  return {
    requirements,
    validRequirementsCount,
    isValid,
    isStarted,
    activeBarsCount,
    activeColor,
    requirementText: requirements.length
      ? locale.passwordRequirementsText.replace(
          '<requirements>',
          requirements.map(requirement => requirement.label).join('; '),
        )
      : '',
    requirementsTooltip: requirements.length
      ? locale.passwordRequirementsTooltip.replace(
          '<requirements>',
          requirements.map(requirement => `\u2022 ${requirement.label}`).join('\n'),
        )
      : '',
  };
};

export const useAgentPasswordComplexity = ({
  password,
  locale,
}: IUseAgentPasswordComplexityPayload): IUseAgentPasswordComplexityData => {
  const settings = useSelector(
    (state: { userSettings: { password_settings?: { agent?: IAgentPasswordSettingsData | null } } }) =>
      state.userSettings.password_settings?.agent,
  );

  return useMemo(() => validateAgentPasswordComplexity({ password, locale, settings }), [password, locale, settings]);
};
